import React from "react";
import ErrorBoundary from "./error-boundary";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentChapter,
  selectFilters,
  selectSelectedLanguages,
  selectSyncChapters,
} from "../../../redux/selectors/filterSelectors";
import { useEffect } from "react";
import {
  selectCurrentSentences,
  selectSentencesLoading,
} from "../../../redux/selectors/sentenceSelectors";
import {
  selectBooksData,
  selectBooksLoading,
  selectCurrentBook,
} from "../../../redux/selectors/bookSelectors";
import SentencesDisplay from "./sentences-display";
import LanguageSkeleton from "./skeletons/language-skeleton";
import { fetchSentencesRequest } from "../../../redux/slices/sentenceSlice";
import { updateChapter as updateChapterAction } from "../../../redux/slices/filterSlice";
import ChapterSelect from "./chapter-select";
import AfricanOnlyInput from "./form-items/african-only-input";
import { selectHasPaid } from "../../../redux/selectors/hasPaidSelectors";
import {
  selectCurrentLanguage,
} from "../../../redux/selectors/languageSelectors";
import PaginationBar from "./pagination-bar";

function LanguageList() {
  const selectedLanguages = useSelector(selectSelectedLanguages);
  const books = useSelector(selectBooksData);
  const dispatch = useDispatch();

  const { searchQuery, itemsPerPage, currentPage, chapters } =
    useSelector(selectFilters);
  const syncChapters = useSelector(selectSyncChapters);
  const hasPaid = useSelector(selectHasPaid);
  const primaryLanguage = selectedLanguages[0];
  const currentLanguage = useSelector(selectCurrentLanguage(primaryLanguage));
  const currentBook = useSelector(selectCurrentBook(currentLanguage?.id));
  const currentChapter = useSelector(selectCurrentChapter(currentBook?.id));
  const currentSentences = useSelector(
    selectCurrentSentences(
      currentBook?.id,
      currentChapter?.chapter_id,
      itemsPerPage
    )
  );

  const sentencesLoading = useSelector(selectSentencesLoading);
  const booksLoading = useSelector(selectBooksLoading);

  useEffect(() => {
    const payload = {
      books, // Array of books
      searchQuery,
      itemsPerPage,
      currentPage,
      chapters, // Array of chapters for books
    };
    // Dispatch fetchSentencesRequest with the required data
    if (books.length) {
      dispatch(fetchSentencesRequest(payload));
    }
  }, [
    dispatch,
    books,
    selectedLanguages,
    searchQuery,
    itemsPerPage,
    currentPage,
    chapters,
  ]);

  const chapterSelectProps =
    syncChapters && currentBook
      ? {
          defaultValue: searchQuery.length
            ? currentSentences?.[0]?.chapter_id
            : currentChapter?.chapter_id || currentSentences?.[0]?.chapter_id,
          hasPaid,
          chapters: currentBook.chapters,
          onChapterChange: (chapterId) =>
            dispatch(
              updateChapterAction({
                book_id: currentBook?.id,
                chapter_id: chapterId,
                syncChapters,
              })
            ),
        }
      : null;

  return (
    <ErrorBoundary>
      {syncChapters && currentBook && (
        <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-2">
          <div className="flex-1 min-w-0 lg:flex-[2]">
            <ChapterSelect {...chapterSelectProps} />
          </div>
          <AfricanOnlyInput
            variant="inline"
            className="lg:w-auto lg:flex-none"
          />
        </div>
      )}
      <div className="mb-4">
        <PaginationBar />
      </div>
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 mb-12">
        {sentencesLoading && booksLoading
          ? selectedLanguages.map((lang, i) => (
              <LanguageSkeleton key={lang} index={i} />
            ))
          : selectedLanguages.map((selectedLang, idx) => {
              return (
                <SentencesDisplay key={idx} language={selectedLang} idx={idx} />
              );
            })}
      </div>
    </ErrorBoundary>
  );
}

export default LanguageList;
